/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import {
  // cloneElement as ReactCloneElement,
  Fragment as ReactFragment,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useCallback
} from 'react'

import {
  CryptoGuideDialog,
  DonateCryptoDialog,
  DonateMethodDialog,
  DonateQRDialog,
  DonateWireDialog,
  LoadingDialog
} from '..'
import addSeconds from 'date-fns/addSeconds'
import format from 'date-fns/format'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import styled, { css } from 'styled-components'

import { ToastContent } from 'src/components/Common/Toast'
import {
  DonorIdentity,
  useDonationIdentityCollectorContext
} from 'src/features/donation/donation-identity-collector'

import bankImage from '../../../assets/bank.png'
import cryptocurrencyImage from '../../../assets/cryptocurrency.png'
// import { useTaxDeductible } from 'src/features/donation/donation-tax'
import paynowImage from '../../../assets/paynow.png'
import {
  ADDRESS_ONE,
  CRYPTO_PAYMENT_EXPIRATION_TIME,
  PAYMENT_EXPIRATION_TIME
} from '../../../constants'
import { ModalConfig, useModalContext } from '../../../contexts/ModalContext'
import { useModalGuideContext } from '../../../contexts/ModalGuideContext'
import { usePollContext } from '../../../contexts/PollProvider'
import { useThemeContext } from '../../../contexts/ThemeContext'
import { useWeb3Context } from '../../../contexts/Web3Context'
import { makeTxId } from '../../../functions/payments'
import { pxToEm, pxToVw } from '../../../functions/units'
import {
  getLocalStorageDonationAmount,
  getLocalStorageDonationWillClaimCoupon,
  postTripleAPaymentRequest,
  postXfersPaymentRequest,
  setLocalStorageNonRedemptionPaymentIds
} from '../../../services/donation'
import {
  CryptoPaymentMutationResponse,
  PaymentType,
  QRPaymentMutationResponse,
  TripleAPaymentMutationBody,
  VAPaymentMutationResponse,
  XfersPaymentMutationBody
} from '../../../types/api'
import Card from '../../Card'
import FlexBox from '../../Common/FlexBox'
import Icon from '../../Icon'

type MethodCardProps = {
  body: ReactNode
  imageSource: string
  title: string
  onClick: (e: ReactMouseEvent<HTMLElement, MouseEvent>) => void
}

const MethodCard = ({ body, imageSource, title, onClick }: MethodCardProps) => {
  return (
    <StyledMethodCard onClick={onClick}>
      <StyledMethodWrap alignItems="center" justifyContent="center" columnGap={24}>
        <StyledMethodIllustration src={imageSource} alt={title} />

        <StyledMethodArticle forwardedAs="article" flexDirection="column">
          <h2>{title}</h2>
          <p>{body}</p>
        </StyledMethodArticle>
      </StyledMethodWrap>
    </StyledMethodCard>
  )
}

const DonateMethod = () => {
  const { colors } = useThemeContext()
  const { openModal, openedModalStack, closeModal, setModalConfig, setModalView } =
    useModalContext()
  const { openModal: openModalGuide, closeModal: closeModalGuide } = useModalGuideContext()
  const { account } = useWeb3Context()
  const { addPollKey } = usePollContext()

  // const { taxDeductible } = useTaxDeductible()
  const { donorIdentity, donorIdentityId, identityIsValid } = useDonationIdentityCollectorContext()

  const mutation = useMutation<
    Response,
    unknown,
    { requestBody: TripleAPaymentMutationBody | XfersPaymentMutationBody; type: PaymentType }
  >(({ requestBody, type }) => {
    if (type === 'qr' || type === 'va') {
      return postXfersPaymentRequest(type, requestBody as XfersPaymentMutationBody)
    } else if (type === 'crypto') {
      return postTripleAPaymentRequest(requestBody as TripleAPaymentMutationBody)
    }

    throw new Error('Unmatch payment type')
  })

  const METHOD_CARD_DATA = [
    {
      imageSource: paynowImage,
      type: 'qr',
      title: 'Donate by Paynow QR',
      body: 'Click here to make your donation via Xfer’s PayNow QR, simply scan the QR code to donate!',
      modalConfig: {
        dialogTitle: 'Donate by Paynow QR'
      }
    },
    {
      imageSource: bankImage,
      type: 'va',
      title: 'Donate by Bank Transfer',
      body: 'Click here to make your donation via transferring money to a unique virtual account.',
      modalConfig: {
        dialogTitle: 'Donate by Bank Transfer'
      }
    },
    {
      imageSource: cryptocurrencyImage,
      type: 'crypto',
      title: 'Donate Cryptocurrency',
      body: (
        <ReactFragment>
          Click here to make your donation via cryptocurrency. Supported tokens:{' '}
          <em>BTC, LNBC, ETH, USDT</em>.
        </ReactFragment>
      ),
      modalConfig: {
        showCloseButton: false,
        showDialogHeader: false,
        showDialogFooter: false
      }
    }
  ]

  const handleMethodCardClick = useCallback(
    async (type: PaymentType, modalConfig: Partial<ModalConfig>) => {
      const willClaimCouponLocalStorage = getLocalStorageDonationWillClaimCoupon()
      const willClaimCoupon =
        willClaimCouponLocalStorage && Number(willClaimCouponLocalStorage) === 1
      if (willClaimCoupon && !account) return

      const donationAmount = getLocalStorageDonationAmount()
      if (!donationAmount) return

      if (!identityIsValid) {
        toast(
          <ToastContent message="We are having problem on retrieving your identity data. Please update your identity data first." />,
          {
            closeOnClick: true,
            autoClose: false,
            type: 'error'
          }
        )
        return
      }

      if (type === 'crypto') {
        const localStorageCryptoGuideDisabled = localStorage.getItem('cryptoGuideDisabled')
        if (!localStorageCryptoGuideDisabled || Number(localStorageCryptoGuideDisabled) === 0) {
          await new Promise((resolve) => {
            openModalGuide(<CryptoGuideDialog onGuideConfirmed={() => resolve(undefined)} />, {
              fullscreen: true,
              showCloseButton: false,
              showDialogFooter: false,
              showDialogHeader: false
            })
          }).then(() => {
            // Mark that the guide has been read, confirmed and we close the guide dialog
            localStorage.setItem('cryptoGuideDisabled', '1')
            console.log('Crypto guide confirmed. Proceed to payment details now.')
            closeModalGuide()
          })
        }
      }

      const now = new Date()
      const formattedRequestDate = format(now, 'dd/MM/yyyy - HH:mm:ss')
      const isoExpiredDate = addSeconds(
        now,
        type === 'crypto' ? CRYPTO_PAYMENT_EXPIRATION_TIME : PAYMENT_EXPIRATION_TIME
      ).toISOString()

      let paymentMethod = ''
      if (type === 'qr') {
        paymentMethod = 'QR Code'
      } else if (type === 'va') {
        paymentMethod = 'Bank Transfer'
      } else if (type === 'crypto') {
        paymentMethod = 'Cryptocurrency'
      } else {
        throw new Error('Unmatch payment type')
      }

      let requestBody: TripleAPaymentMutationBody | XfersPaymentMutationBody
      if (type === 'qr' || type === 'va') {
        requestBody = {
          amount: donationAmount,
          user_wallet_address: willClaimCoupon && account ? account : ADDRESS_ONE,
          tx_id: makeTxId(),
          description: `${paymentMethod} | ${formattedRequestDate}`,
          expired_at: isoExpiredDate,
          user_info: { ...(donorIdentity as DonorIdentity) },
          user_profile_id: donorIdentityId as string
        } as XfersPaymentMutationBody
      } else if (type === 'crypto') {
        requestBody = {
          amount: donationAmount,
          user_wallet_address: willClaimCoupon && account ? account : ADDRESS_ONE,
          tx_id: makeTxId(),
          description: `${paymentMethod} | ${formattedRequestDate}`,
          user_info: { ...(donorIdentity as DonorIdentity) }
        } as TripleAPaymentMutationBody
      } else {
        throw new Error('Unmatch payment type')
      }

      // Request message has been formed. Open new dialog and set its' view to
      // a loading spinner while waiting the request to be sent.
      if (openedModalStack.at(-1)) {
        closeModal()
      }
      openModal(<LoadingDialog />, requestBody.tx_id, {
        fullscreen: false,
        showCloseButton: false,
        showDialogFooter: false,
        showDialogHeader: false
      })

      try {
        const response = await mutation.mutateAsync({ type, requestBody })
        const result = await response.json()

        if (!willClaimCoupon || !account) {
          setLocalStorageNonRedemptionPaymentIds(result.id)
        }

        addPollKey(result.id)

        setModalView(
          type === 'qr' ? (
            <DonateQRDialog
              expiredAt={(result as QRPaymentMutationResponse).attributes.expired_at}
              qrCodeImage={
                (result as QRPaymentMutationResponse).attributes.payment_method
                  .qr_code_display_image
              }
              txId={requestBody.tx_id}
              id={result.id}
            />
          ) : type === 'va' ? (
            <DonateWireDialog
              expiredAt={(result as VAPaymentMutationResponse).attributes.expired_at}
              paymentAmount={(result as VAPaymentMutationResponse).attributes.amount}
              transferInstructions={
                (result as VAPaymentMutationResponse).attributes.payment_method.instructions
              }
              txId={requestBody.tx_id}
            />
          ) : (
            <DonateCryptoDialog hostedUrl={(result as CryptoPaymentMutationResponse).hosted_url} />
          )
        )

        setModalConfig((prev) => ({
          ...prev,
          showCloseButton: true,
          showDialogFooter: true,
          showDialogHeader: true,
          ...modalConfig
        }))
      } catch (e: any) {
        console.error(e)
        setModalView(<DonateMethodDialog />)
        setModalConfig((prev) => ({
          ...prev,
          dialogTitle: 'Choose donation method',
          showCloseButton: true,
          showDialogFooter: true,
          showDialogHeader: true
        }))
      }
    },
    [
      account,
      addPollKey,
      closeModal,
      closeModalGuide,
      donorIdentity,
      donorIdentityId,
      identityIsValid,
      mutation,
      openModal,
      openModalGuide,
      openedModalStack,
      setModalConfig,
      setModalView
    ]
  )

  return (
    <>
      <StyledArticle>
        <FlexBox as="header" columnGap={12} alignItems="center">
          <Icon icon="caret-right-circle" color={colors.primary600} size={26} />
          <h2>Where is my donation going towards?</h2>
        </FlexBox>

        <p>
          Funds raised will help SG Enable catalyse new programmes at the Enabling Village,
          Singapore’s first inclusive community space for persons with disabilities.
        </p>
      </StyledArticle>

      <StyledHorizontalLine />

      <FlexBox flexDirection="column" rowGap={16}>
        {METHOD_CARD_DATA.map((method, i) => (
          <MethodCard
            imageSource={method.imageSource}
            title={method.title}
            body={method.body}
            onClick={() => handleMethodCardClick(method.type as PaymentType, method.modalConfig)}
            key={`methodCard_${i}`}
          />
        ))}
      </FlexBox>
    </>
  )
}

export default DonateMethod

const StyledMethodCard = styled(Card)`
  width: 100%;
  padding: ${pxToVw(33)} ${pxToVw(40)};
  // will-change: transform;
  // transform: scale(1);
  // transition: transform 0.125s ease-in;

  &:hover {
    // transform: scale(1.025);
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding: ${pxToVw(33, 720)} ${pxToVw(40, 720)};
    `}
  `}
`

const StyledMethodWrap = styled(FlexBox)`
  height: 100%;
`

const StyledMethodIllustration = styled.img`
  height: ${pxToVw(133)};
  object-fit: cover;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${pxToVw(133, 720)};
    `}
  `}
`

const StyledMethodArticle = styled(FlexBox)`
  width: 18.75vw;

  > h2 {
    margin-top: 0;
    margin-bottom: ${pxToVw(18)};
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.header3)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray600};
  }

  > p {
    margin: 0;
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body1)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: ${pxToVw(330, 720)};

      > h2 {
        margin-bottom: ${pxToVw(18, 720)};
        font-size: ${pxToVw(theme.units.fontSize.header3, 720)};
      }

      > p {
        font-size: ${pxToVw(theme.units.fontSize.body1, 720)};
      }
    `}
  `}
`

const StyledArticle = styled.article`
  h2 {
    margin: 0;
    font-size: ${pxToVw(24)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray500};
  }

  p {
    margin: 0;
    margin-top: ${pxToVw(15)};
    font-size: ${pxToVw(18)};
    line-height: ${pxToEm(24, 18)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      h2 {
        font-size: ${pxToVw(24, 720)};
      }

      p {
        margin-top: ${pxToVw(15, 720)};
        font-size: ${pxToVw(18, 720)};
      }
    `}
  `}
`

const StyledHorizontalLine = styled.hr`
  margin-block: 1.5625vw;
  border: none;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(30, 720)};
    `}
  `}
`
